<template>
  <div class="invoices-page">
    <PageWrapperComponent>
      <DataTable class="invoices-list">
        <template #header>
          <th scope="col" class="invoices-list__col invoices-list__col--date">Date</th>
          <th scope="col" class="invoices-list__col invoices-list__col--id">Invoice ID</th>
          <th scope="col" class="invoices-list__col invoices-list__col--total">Total</th>
          <th scope="col" class="invoices-list__col invoices-list__col--status">Status</th>
          <th scope="col" class="invoices-list__actions" aria-label="Actions" />
        </template>
        <tr v-if="loading">
          <td colspan="5" class="invoices-list__state data-table__loading" aria-label="Loading"><LoadingComponent /></td>
        </tr>
        <tr v-for="invoice in invoices" v-else :key="invoice.id" class="invoices-list__row">
          <td class="invoices-list__col invoices-list__col--date">
            <TypographyComponent variant="title-large">{{ formatDateOnlyLong(invoice.invoice_date) }}</TypographyComponent>
          </td>
          <td class="invoices-list__col invoices-list__col--id">
            <TypographyComponent variant="body-medium">{{ invoice.id }}</TypographyComponent>
          </td>
          <td class="invoices-list__col invoices-list__col--total">
            <TypographyComponent variant="body-medium">{{ formatInvoiceTotal(invoice) }}</TypographyComponent>
          </td>
          <td class="invoices-list__col invoices-list__col--status">
            <FlexComponent :gap="8" justify="end" align="center">
              <el-tooltip placement="bottom-start" effect="light" :show-arrow="false" :disabled="invoice.status !== InvoiceStatusEnum.Paid">
                <BadgeComponent class="invoices-list__badge" :variant="invoiceStatusVariant(invoice.status)">{{
                  invoiceStatusLabel(invoice.status)
                }}</BadgeComponent>
                <template #content
                  ><TypographyComponent variant="label-large">Paid {{ formatDateOnlyLong(invoice.paid_at) }}</TypographyComponent></template
                >
              </el-tooltip>
              <ButtonComponent
                v-if="paymentIsRetryable(invoice)"
                variant="text"
                :is-loading="retryingPayment[invoice.id]"
                @click="retryPayment(invoice)"
              >
                <template #iconLeft
                  ><ElIcon class="danger" size="24"><RefreshIcon /></ElIcon
                ></template>
                <TypographyComponent is="span" variant="label-large" color="var(--danger)">Retry</TypographyComponent>
              </ButtonComponent>
            </FlexComponent>
          </td>
          <td class="invoices-list__actions">
            <el-popover v-if="invoice.credit_invoice_url" trigger="click" :show-arrow="false" width="auto">
              <FlexComponent direction="column" align="start" data-testid="download-popover">
                <ButtonComponent variant="text" :href="invoice.credit_invoice_url" :target="`credit_note_${invoice.id}`"
                  ><template #iconLeft
                    ><ElIcon size="24"><Download /></ElIcon></template
                  >Credit Note</ButtonComponent
                >
                <ButtonComponent variant="text" :href="invoice.url" :target="`invoice_${invoice.id}`"
                  ><template #iconLeft
                    ><ElIcon size="24"><Download /></ElIcon></template
                  >Invoice</ButtonComponent
                >
              </FlexComponent>
              <template #reference
                ><ButtonComponent is-icon variant="text"><Download /></ButtonComponent
              ></template>
            </el-popover>
            <ButtonComponent v-else is-icon variant="text" :href="invoice.url" :target="`invoice_${invoice.id}`"
              ><Download
            /></ButtonComponent>
          </td>
        </tr>
      </DataTable>
    </PageWrapperComponent>
  </div>
</template>

<script lang="ts">
import { Download } from '@element-plus/icons-vue'
import { BadgeComponent, type BadgeVariant, ButtonComponent, DataTable, PageWrapperComponent } from '@metapic-apps/shared'
import { ElIcon } from 'element-plus'
import { mapStores } from 'pinia'
import { mapGetters, mapState } from 'vuex'

import RefreshIcon from '@/assets/icons/refresh.svg?component'
import Repository from '@/legacy/repositories/Repository'
import { formatDateOnlyLong } from '@/lib/formatters'
import { formatMoneyMinorForAdvertiser } from '@/lib/formatters/money.formatter'
import { useErrorStore } from '@/stores/error.stores'
import { InvoiceStatusEnum } from '@/types/invoice'
import { FlexComponent, LoadingComponent, TypographyComponent } from '@/ui-library'

export default {
  name: 'Invoices',
  components: {
    BadgeComponent,
    ButtonComponent,
    DataTable,
    ElIcon,
    FlexComponent,
    LoadingComponent,
    Download,
    PageWrapperComponent,
    RefreshIcon,
    TypographyComponent,
  },
  data() {
    return { InvoiceStatusEnum, retryingPayment: {} as Record<number, boolean> }
  },
  computed: { ...mapState('invoices', ['invoices', 'loading']), ...mapGetters('user', ['token']), ...mapStores(useErrorStore) },
  created() {
    this.$store.dispatch('invoices/fetchInvoices')
  },
  beforeUpdate() {
    if (!this.invoices.length) {
      this.$router.push({ name: 'overview' })
    }
  },
  methods: {
    formatInvoiceTotal(invoice: { total: number; currency: string }) {
      return formatMoneyMinorForAdvertiser({ amount: invoice.total / 100, currency: invoice.currency }, { allowZero: true })
    },
    invoiceStatusLabel(status: string) {
      switch (status?.toLowerCase()) {
        case InvoiceStatusEnum.Failed.toLowerCase():
          return 'Failed'
        case InvoiceStatusEnum.Pending.toLowerCase():
          return 'Pending'
        case InvoiceStatusEnum.Paid.toLowerCase():
          return 'Paid'
        case InvoiceStatusEnum.Credited:
          return 'Credited'
        default:
          return 'Unknown'
      }
    },
    invoiceStatusVariant(status: string): BadgeVariant {
      switch (status?.toLowerCase()) {
        case InvoiceStatusEnum.Failed.toLowerCase():
          return 'danger'
        case InvoiceStatusEnum.Paid.toLowerCase():
          return 'success'
        case InvoiceStatusEnum.Credited:
          return 'warning'
        default:
          return 'neutral'
      }
    },
    paymentIsRetryable(invoice: { status: string }) {
      return invoice.status === InvoiceStatusEnum.Failed
    },
    async retryPayment(invoice: { store_id: string; id: number }) {
      this.retryingPayment[invoice.id] = true
      try {
        await Repository({ Authorization: this.token }).post(`/stores/${invoice.store_id}/invoices/${invoice.id}/retry-payment`)
      } catch {
        this.errorStore.setError({})
      } finally {
        this.retryingPayment[invoice.id] = false
      }
      await this.$store.dispatch('invoices/fetchInvoices')
    },
    formatDateOnlyLong,
  },
}
</script>

<style lang="scss" scoped>
.invoices-list__col {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.invoices-list__col--date {
  width: 165px;
}
.invoices-list__col--id {
  width: auto;
}
.invoices-list__col--total {
  width: 120px;
  text-align: right;
}
th.invoices-list__col--total,
th.invoices-list__col--status {
  text-align: right;
}
.invoices-list__col--status {
  width: 200px;
  text-align: right;
}
.invoices-list__actions {
  width: 88px;
  text-align: right;
}
.invoices-list__state {
  color: rgba(0, 0, 0, 0.32);
  text-align: center;
}
.danger {
  color: var(--danger);
}
.danger :deep(path) {
  fill: var(--danger);
}
</style>
